import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CONF = { high: "bg-emerald-600", medium: "bg-amber-500", low: "bg-slate-400", none: "bg-red-500" };

// Mines file names / notes / ClickUp task names for a street address on auto-WOs that still have none.
export default function GuessAddressesPanel({ onApplied }) {
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/guess-addresses");
      setData(r.data);
      const p = {};
      r.data.rows.forEach(row => { if (row.proposed) p[row.id] = { address: row.proposed, on: row.confidence === "high" || row.confidence === "medium" }; });
      setPicks(p);
      toast.success(`${r.data.total} WOs scanned · ${r.data.by_confidence.high} high · ${r.data.by_confidence.medium} medium`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Guess failed")); }
    finally { setBusy(false); }
  };

  const selected = Object.entries(picks).filter(([, v]) => v.on && v.address?.trim());
  const apply = async () => {
    if (!window.confirm(`Set addresses on ${selected.length} work orders? Only the checked rows are written. No notifications.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/apply-guesses", { picks: selected.map(([id, v]) => ({ id, property_address: v.address.trim() })) });
      toast.success(`Set ${r.data.updated} addresses`);
      setData(null); setPicks({}); onApplied?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Apply failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 border-2 border-violet-500 bg-violet-50 rounded space-y-3" data-testid="guess-addresses-panel">
      <div className="font-black uppercase tracking-widest text-violet-900 text-sm">🔎 Guess Address from Files &amp; Notes</div>
      <div className="text-xs text-slate-700">
        For auto-WOs AppFolio didn't cover: reads the ClickUp task names, invoice/email notes and photo file names on each WO, finds street addresses, and matches them to properties you already have. You confirm every row before anything is written.
      </div>
      {!data && (
        <button onClick={run} disabled={busy} data-testid="guess-addresses-btn"
          className="h-10 px-4 bg-violet-700 hover:bg-violet-800 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full">
          {busy ? "Scanning…" : "Scan WOs without an address"}
        </button>
      )}
      {data && (
        <div className="space-y-2">
          <div className="text-xs font-mono flex gap-3 flex-wrap" data-testid="guess-summary">
            <span><b>{data.total}</b> without address</span>
            <span className="text-emerald-700"><b>{data.by_confidence.high}</b> high</span>
            <span className="text-amber-700"><b>{data.by_confidence.medium}</b> medium</span>
            <span className="text-slate-500"><b>{data.by_confidence.low}</b> low</span>
            <span className="text-red-700"><b>{data.by_confidence.none}</b> no clue</span>
          </div>
          <div className="max-h-[28rem] overflow-auto bg-white border border-violet-200 rounded divide-y divide-slate-100">
            {data.rows.map(r => {
              const p = picks[r.id] || { address: "", on: false };
              return (
                <div key={r.id} className="p-2 text-xs flex gap-2 items-start" data-testid={`guess-row-${r.work_order_number}`}>
                  <input type="checkbox" className="mt-1" checked={!!p.on} disabled={!p.address?.trim()}
                    onChange={e => setPicks(s => ({ ...s, [r.id]: { ...p, on: e.target.checked } }))} data-testid={`guess-check-${r.work_order_number}`}/>
                  <div className="w-16 shrink-0">
                    <div className="font-mono font-black">{r.work_order_number}</div>
                    <span className={`inline-block mt-0.5 px-1 text-[9px] font-black uppercase text-white ${CONF[r.confidence]}`}>{r.confidence}</span>
                    <div className="text-[10px] text-slate-500">{r.files}f · {r.notes}n</div>
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="truncate text-slate-700">{r.title || <i className="text-slate-400">no title</i>}</div>
                    <input value={p.address} placeholder="Start typing an address…" list="known-addresses" autoComplete="off" data-testid={`guess-input-${r.work_order_number}`}
                      onChange={e => setPicks(s => ({ ...s, [r.id]: { address: e.target.value, on: !!e.target.value.trim() } }))}
                      className="w-full h-8 px-2 border-2 border-slate-300 rounded font-bold"/>
                    {r.alternatives?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {r.alternatives.map(a => (
                          <button key={a.address} onClick={() => setPicks(s => ({ ...s, [r.id]: { address: a.address, on: true } }))}
                            className="px-1.5 py-0.5 border border-slate-300 rounded text-[10px] hover:bg-slate-100">{a.address}</button>
                        ))}
                      </div>
                    )}
                    {r.evidence?.[0] && <div className="text-[10px] text-slate-500 line-clamp-2"><b>{r.evidence[0].source}:</b> …{r.evidence[0].snippet}…</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setData(null); setPicks({}); }} className="h-10 px-4 border-2 border-slate-300 text-xs font-black uppercase tracking-widest rounded">Cancel</button>
            <button onClick={apply} disabled={busy || selected.length === 0} data-testid="apply-guesses"
              className="flex-1 h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a]">
              Set {selected.length} Address{selected.length === 1 ? "" : "es"} — checked rows only
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
